import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const noticeVariants = cva(
    "flex flex-col gap-1 rounded-md border px-3 py-2 text-xs leading-[1.55]",
    {
        variants: {
            /**
             * `would-exceed` is guidance ahead of an action the user can still
             * change; `rejected` reports an action the server refused.
             */
            variant: {
                "would-exceed":
                    "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100",
                rejected:
                    "border-destructive/40 bg-destructive/10 text-destructive",
            },
        },
        defaultVariants: {
            variant: "would-exceed",
        },
    }
);

const FOCUS_COPY =
    "Polly gives more thorough answers when a chat stays focused on fewer documents.";

export interface DirectDocumentBudgetNoticeProps extends VariantProps<
    typeof noticeVariants
> {
    variant: "would-exceed" | "rejected";
    limit: number;
    /** How many of the current selection are new to this chat. */
    newCount?: number;
    className?: string;
}

/**
 * Blocked guidance.
 *
 * Both variants carry `role="alert"`: each appears in response to a user
 * action that did not go through, which is exactly the case a screen-reader
 * user cannot discover by looking. Rendering the text somewhere the focus is
 * not, without announcing it, would leave the block invisible to them.
 */
export function DirectDocumentBudgetNotice({
    variant,
    limit,
    newCount,
    className,
}: DirectDocumentBudgetNoticeProps) {
    const isWouldExceed = variant === "would-exceed";

    return (
        <div
            role="alert"
            data-slot="direct-document-budget-notice"
            data-variant={variant}
            className={cn(noticeVariants({ variant }), className)}
        >
            <p className="font-medium">
                {isWouldExceed
                    ? "This would go over the document limit"
                    : "Couldn't attach — over the document limit"}
            </p>
            <p>
                {isWouldExceed
                    ? `${FOCUS_COPY} This chat already holds ${limit} documents.`
                    : `${FOCUS_COPY} Your selections are still here — remove one or start a new chat.`}
            </p>
            {isWouldExceed && newCount != null && (
                <p className="tabular-nums">
                    {newCount} of your selected documents are new to this chat.
                </p>
            )}
        </div>
    );
}
